import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"

import { cn } from "../../lib/utils"
import { buttonVariants } from "@/components/ui/button"

// Constants for drag-to-dismiss behavior
const DRAG_THRESHOLD = 80;
const DRAG_HANDLE_HEIGHT = 32; // h-8 = 32px

const AlertDialog = AlertDialogPrimitive.Root

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

const AlertDialogPortal = AlertDialogPrimitive.Portal

const AlertDialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref} />
))
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName

const AlertDialogContent = React.forwardRef(({ className, onOpenChange, children, ...props }, ref) => {
  const [dragY, setDragY] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const startY = React.useRef(0);
  const handleRef = React.useRef(null);
  const dragYRef = React.useRef(0);

  // Keep dragYRef in sync
  React.useEffect(() => {
    dragYRef.current = dragY;
  }, [dragY]);

  const handleMouseDown = React.useCallback((e) => {
    if (handleRef.current && handleRef.current.contains(e.target)) {
      setIsDragging(true);
      startY.current = e.clientY;
      setDragY(0);
      dragYRef.current = 0;
    }
  }, []);

  const handleMouseMove = React.useCallback((e) => {
    const deltaY = e.clientY - startY.current;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  }, []);

  const handleMouseUp = React.useCallback(() => {
    if (dragYRef.current > DRAG_THRESHOLD && onOpenChange) {
      onOpenChange(false);
    }
    setIsDragging(false);
    setDragY(0);
    dragYRef.current = 0;
  }, [onOpenChange]);

  const handleTouchStart = React.useCallback((e) => {
    if (handleRef.current && handleRef.current.contains(e.target)) {
      setIsDragging(true);
      startY.current = e.touches[0].clientY;
      setDragY(0);
      dragYRef.current = 0;
    }
  }, []);

  const handleTouchMove = React.useCallback((e) => {
    const deltaY = e.touches[0].clientY - startY.current;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  }, []);

  const handleTouchEnd = React.useCallback(() => {
    if (dragYRef.current > DRAG_THRESHOLD && onOpenChange) {
      onOpenChange(false);
    }
    setIsDragging(false);
    setDragY(0);
    dragYRef.current = 0;
  }, [onOpenChange]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        style={{
          transform: `translate(-50%, calc(-50% + ${dragY}px))`,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out',
        }}
        {...props}
        onOpenChange={onOpenChange}
      >
        {/* Drag Handle */}
        <div
          ref={handleRef}
          className="absolute top-0 left-0 right-0 flex items-center justify-center cursor-grab active:cursor-grabbing"
          style={{ height: `${DRAG_HANDLE_HEIGHT}px` }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div className="w-10 h-1 bg-muted-foreground/50 rounded-full" />
        </div>

        <div className="pt-2">
          {children}
        </div>
      </AlertDialogPrimitive.Content>
    </AlertDialogPortal>
  );
})
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName

const AlertDialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-2 text-center sm:text-left", className)}
    {...props} />
)
AlertDialogHeader.displayName = "AlertDialogHeader"

const AlertDialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
AlertDialogFooter.displayName = "AlertDialogFooter"

const AlertDialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title ref={ref} className={cn("text-lg font-semibold", className)} {...props} />
))
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName

const AlertDialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName

const AlertDialogAction = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action ref={ref} className={cn(buttonVariants(), className)} {...props} />
))
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName

const AlertDialogCancel = React.forwardRef(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonVariants({ variant: "outline" }), "mt-2 sm:mt-0", className)}
    {...props} />
))
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
